<template>
  <MainLayout
    v-scroll="onScroll"
    scroll-toolbar
    :bg-color="layoutMode === 'timeline' ? '' : 'white'"
    :main-layout-scroll-class="layoutMode === 'hooks' ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden'"
    no-content-padding
    no-bottom-content-padding
    :style="layoutStyleOverride"
    :use-stable-scroll-gutter="useStableScrollGutter"
  >
    <!-- Layout Toolbar -->
    <template #toolbar>
      <div>
        <div class="flex items-center px-5 pt-5 pb-2.5 gap-4">
          <!-- BRAND ROUTE SWITCHER -->
          <div class="flex items-center">
            <button class="px-1.5" @click="navigateBack">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M11.3217 13.3333L8.57757 10.5893C8.25214 10.2638 8.25214 9.73618 8.57757 9.41074L11.3217 6.66666"
                stroke="#5E6678" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <!-- Folder switcher (loading) -->
            <div v-if="subscriptionStatusLoading" class="flex items-center pl-2 pr-1 py-1.5">
              <FolderIcon class="text-icon-normal mr-2" />
              <div class="skeleton w-24 h-4 rounded-md" />
            </div>
            <div v-else-if="userIsSubscribed" class="flex items-center fade-in">
              <!-- Folder switcher (dropdown opener) -->
              <button v-if="!folderSwitcherExpanded" class="brand-route-switcher rounded-md flex items-center pl-2 pr-1 py-1.5"
              :class="{'enabled' : folderRoutes.length > 0}"
              @click="() => { if (folderRoutes.length > 0) { setFolderSwitcherExpanded(true); setBrandSwitcherExpanded(false) }}">
                <FolderIcon class="text-icon-normal mr-2" />
                <BaseText type="label" size="sm" class="text-text-muted mr-1 truncate" style="max-width: 200px">
                  {{ folder.name }}
                </BaseText>
                <ChevronGrabberIcon v-if="folderRoutes.length > 0" class="text-icon-disabled" />
              </button>
              <!-- Folder switcher (dropdown) -->
              <div v-else ref="folder-switcher-spacer" class="relative h-8">
                <div ref="folder-switcher" v-on-clickaway="() => { setFolderSwitcherExpanded(false) }"
                class="brand-switcher-dropdown absolute w-max -top-1 -left-1 flex flex-col gap-1 px-1 pt-1 rounded-lg bg-neutral-25 z-50">
                  <button class="flex items-center rounded-md pl-2 pr-1 py-1.5 box-shadow bg-white"
                  @click="setFolderSwitcherExpanded(false)">
                    <FolderIcon class="text-icon-normal mr-2" />
                    <BaseText type="label" size="sm" class="text-text-muted mr-1 truncate" style="max-width: 200px">
                      {{ folder.name }}
                    </BaseText>
                    <ChevronGrabberIcon class="ml-auto text-icon-disabled" />
                  </button>
                  <div class="flex flex-col gap-1 overflow-y-scroll scrollbar-hide" style="max-height: 236px"
                  :class="folderRoutes.length > 6 ? 'pb-2' : 'pb-1'">
                    <router-link v-for="folderRoute in folderRoutes" :key="`folder-${folderRoute.name}`"
                    :to="{name: 'SpyderView', params: { id: folderRoute.brandId, view: currentView, mode: currentMode }}"
                    class="flex items-center rounded-md pl-2 pr-1 py-1.5 transition-colors hover:bg-neutral-50">
                      <FolderIcon class="text-icon-normal mr-2" />
                      <BaseText type="label" size="sm" class="text-text-muted mr-1 truncate" style="max-width: 200px">
                        {{ folderRoute.name }}
                      </BaseText>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
            <!-- divider -->
            <svg v-if="userIsSubscribed || subscriptionStatusLoading" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M11 4.00003L8.91622 15.8177" stroke="#C1C7D0" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <!-- Brand switcher (loading) -->
            <div v-if="!headerBrand" class="flex items-center pl-2 pr-1 py-1.5">
              <div class="skeleton w-36 h-5 rounded-md" />
            </div>
            <div v-else class="flex items-center fade-in">
              <!-- Brand switcher (dropdown opener) -->
              <button v-if="!brandSwitcherExpanded" class="brand-route-switcher rounded-md flex items-center pl-2 pr-1 py-1.5"
              :class="{'enabled' : folderBrands.length > 0}"
              @click="() => { if (folderBrands.length > 0) { setBrandSwitcherExpanded(true); setFolderSwitcherExpanded(false) }}">
                <BrandAvatar :avatar-src="headerBrand.avatar_url" :brand-name="headerBrand.name" :width="20" :height="20"
                class="mr-2" style="border-radius: 4.5px" />
                <BaseText type="label" size="sm" class="text-text-muted mr-1 truncate" style="max-width: 200px">
                  {{ headerBrand.name }}
                </BaseText>
                <ChevronGrabberIcon v-if="folderBrands.length > 0" class="text-icon-disabled" />
              </button>
              <!-- Brand switcher (dropdown) -->
              <div v-else ref="brand-switcher-spacer" class="relative h-8">
                <div ref="brand-switcher" v-on-clickaway="() => { setBrandSwitcherExpanded(false) }"
                class="brand-switcher-dropdown absolute w-max -top-1 -left-1 flex flex-col gap-1 px-1 pt-1 rounded-lg bg-neutral-25 z-50">
                  <button class="flex items-center rounded-md pl-2 pr-1 py-1.5 box-shadow bg-white"
                  @click="setBrandSwitcherExpanded(false)">
                    <BrandAvatar :avatar-src="headerBrand.avatar_url" :brand-name="headerBrand.name" :width="20" :height="20"
                    class="mr-2" style="border-radius: 4.5px" />
                    <BaseText type="label" size="sm" class="text-text-muted mr-1 truncate" style="max-width: 200px">
                      {{ headerBrand.name }}
                    </BaseText>
                    <ChevronGrabberIcon class="ml-auto text-icon-disabled" />
                  </button>
                  <div class="flex flex-col gap-1 overflow-y-scroll scrollbar-hide" style="max-height: 236px"
                  :class="folderBrands.length > 6 ? 'pb-2' : 'pb-1'">
                    <router-link v-for="folderBrand in folderBrands" :key="`switcher-${folderBrand.id}`"
                    :to="{name: 'SpyderView', params: { id: folderBrand.id, view: currentView, mode: currentMode }}"
                    class="flex items-center rounded-md pl-2 pr-1 py-1.5 transition-colors hover:bg-neutral-50">
                      <BrandAvatar :avatar-src="folderBrand.avatar_url" :brand-name="folderBrand.name" :width="20" :height="20"
                      class="mr-2" style="border-radius: 4.5px" />
                      <BaseText type="label" size="sm" class="text-text-muted mr-1 whitespace-nowrap truncate" style="max-width: 200px">
                        {{ folderBrand.name }}
                      </BaseText>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Trackable brands have no ad data for these to date -->
          <div v-if="!isTrackableBrand" class="flex flex-wrap gap-x-4 gap-y-0.5" style="max-height: 40px;">
            <!-- Last updated label -->
            <div v-if="!headerBrand" class="skeleton w-24 h-6 rounded-full" />
            <div v-else class="bg-neutral-25 px-2.5 py-1 rounded-full fade-in">
              <BaseText type="body" size="xs" class="text-text-muted">
                {{ getBrandLastUpdated() }}
              </BaseText>
            </div>
            <!-- Data since label -->
            <div v-if="!headerBrand" class="skeleton w-40 h-6 rounded-full" />
            <div v-else class="bg-neutral-25 px-2.5 py-1 rounded-full fade-in">
              <BaseText type="body" size="xs" class="text-text-muted">
                Data since {{ formatDate(headerBrand.scraping_data_since) }}
              </BaseText>
            </div>
          </div>
          <div class="ml-auto flex items-center gap-3">
            <button id="spyder-view-tutorial-button" class="flex items-center gap-1.5 pl-2 py-1.5 pr-3 rounded-md transition-colors hover:bg-neutral-25"
            @click="tutorialTimestamp = 74, showTutorialModal = true">
              <PlayIcon class="text-icon-normal" />
              <div class="text-sm leading-5 font-medium text-text-muted">
                Tutorial
              </div>
            </button>
            <BasePrimaryButton
              v-if="headerBrand && !userIsSubscribed"
              class="fade-in"
              text="Track Brand"
              :icon="trackIcon"
              @click="openTrackBrandModal"
            />
          </div>
        </div>
        <!-- SUBVIEW TABS -->
        <div class="flex gap-2 px-3 pb-3 pt-1 border-b border-border-normal">
          <BaseTooltipV2 v-for="view in topbarViews" :key="`topbar-view-${view.key}`" :tooltip-enabled="view.disabled"
          :text="view.disabledText" position="bottom" :delay="750" :z-index="40002" :offset="0">
            <button class="flex items-center p-1 rounded-lg transition-colors duration-100"
            :class="view.disabled ? 'text-text-disabled cursor-default' : view.isActive ? 'bg-neutral-25 text-text-muted' : 'text-text-normal hover:text-text-muted hover:bg-neutral-25'"
            @click="view.action" :disabled="view.disabled">
              <component :is="view.icon" />
              <BaseText type="label" size="sm" class="px-1">
                {{ view.name }}
              </BaseText>
            </button>
          </BaseTooltipV2>
        </div>
      </div>
    </template>
    <!-- Layout Content -->
    <template #content>
      <div class="w-full flex items-stretch" style="min-height: calc(100vh - 142px);">
        <!-- Sidebar Navigation -->
        <div v-if="showSidebarNav" class="brand-sidebar-nav border-r border-border-normal" :class="{'collapsed': navCollapsed}">
          <div class="nav-body w-full flex flex-col gap-2 p-3 min-w-0">
            <BaseTooltipV2 v-for="mode in sidebarModes" :key="`sidebar-mode-${mode.key}`" class="w-full h-7"
            :tooltip-enabled="navCollapsed" :text="mode.name" position="right" alignment="center" :z-index="40001">
              <button class="relative w-full h-7 rounded-lg transition-colors duration-100 overflow-hidden"
              :class="mode.isActive ? 'text-text-muted bg-neutral-25' : 'text-text-normal hover:text-text-muted hover:bg-neutral-25'"
              @click="mode.action">
                <div class="absolute top-0 left-0 h-7 flex items-center gap-1.5 p-1" style="width: 180px">
                  <component :is="mode.icon" class="flex-shrink-0" />
                  <BaseText type="label" size="sm" class="nav-btn-text flex-shrink-0">
                    {{ mode.name }}
                  </BaseText>
                  <div v-if="mode.isNew" class="px-1.5 py-0.5 rounded-full bg-primary-red-10 flex-shrink-0">
                    <BaseText type="label" size="xs" class="text-primary-red-300">New</BaseText>
                  </div>
                </div>
              </button>
            </BaseTooltipV2>
          </div>
          <div class="collapse-toggle-container">
            <button class="collapse-toggle" @click="toggleNavCollapse">
              <ChevronIcon class="text-icon-normal transform transition-transform duration-300"
              :style="{ transform: navCollapsed ? 'scaleX(-1) rotate(90deg)' : 'scaleX(1) rotate(90deg)' }" />
            </button>
          </div>
          <!-- <div class="nav-footer p-3">
            NAV FOOTER CONTENT
          </div> -->
        </div>
        <div class="flex-grow min-w-0">
          <!-- The state for when a brand is not being tracked, but is trackable -->
          <TrackableBrandOverview
            v-if="currentView === 'overview' && isTrackableBrand"
            :brand="brand"
          />
          <BrandOverviewView
            v-else-if="currentView === 'overview'"
            :brand="brand"
            @analyzeAdRanks="analyzeAdRanks"
            @viewMetaAds="viewMetaAds"
            @viewPartnerAds="viewPartnerAds"
          />
          <CommunityAdsBrandView
            v-else-if="currentView === 'community-ads'"
            :brand="brand"
            :blocked="showSpyderFixModal"
          />
          <AdLibraryBrandView
            v-else-if="currentMode === 'ad-library'"
            :brand="brand"
            :blocked="showSpyderFixModal"
            @tabSwitch="handleTabSwitch"
          />
          <TimelineBrandView
            v-else-if="currentMode === 'timeline'"
            :brand="brand"
            :blocked="showSpyderFixModal"
          />
          <PartnershipAdsBrandView 
            v-else-if="currentMode === 'partnership-ads'" 
            :brand="brand"
            :blocked="showSpyderFixModal"
          />
          <HooksBrandView 
            v-else-if="currentMode === 'hooks'" 
            :brand="brand"
          />
          <div v-else class="px-4 pt-4 pb-4">
            <AdRankBrandView
              v-if="currentMode === 'ad-rankings'"
              :brand="brand"
            />
            <CreativeTestsBrandView
              v-else-if="currentMode === 'creative-tests'"
              :brand="brand"
              @openTutorialModal="tutorialTimestamp = 99, showTutorialModal = true"
            />
            <LandingPagesBrandView
              v-else-if="currentMode === 'landing-pages'"
              :brand="brand"
              :initial-landing-page-url="pendingLandingPageUrl"
            />
            <WinningAdsBrandView 
              v-else-if="currentMode === 'winning-ads'" 
            />
          </div>
        </div>
      </div>
      <BaseTutorialModal
        v-if="showTutorialModal"
        video-url="https://www.youtube.com/embed/zNj71_-IIVk?si=C5PRycUlLRkX86l8"
        :timestamp="tutorialTimestamp"
        @close="showTutorialModal = false"
      />
      <TrackBrandModal
        v-if="showTrackBrandModal && brand?.id"
        :brand="brand"
        @subscribed="handleBrandSubscribed"
        @close="showTrackBrandModal = false"
      />
      <FixSpyderModal
        v-if="showSpyderFixModal"
        :spyder-limit="spyderLimit"
        @close="showSpyderFixModal = false"
      />
    </template>
  </MainLayout>
</template>
<script>
import ForeplayAPI from '@/api/foreplayServer'
import { mapGetters, mapActions } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import { getBrandsInFolder, getUnfolderedBrands, findFolderForBrand } from '@/utils/spyder/brandFolderUtils'
import { BRAND_AD_DATA_STATES, getBrandAdDataState } from '@/utils/spyder/brandUtils'
import moment from 'moment'

// Components
import MainLayout from '@/layouts/MainLayout.vue'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import FixSpyderModal from '../../components/spyderV2/FixSpyderModal.vue'
import TrackBrandModal from '../../components/spyderV2/TrackBrandModal.vue'
import BrandAvatar from '../../components/brands/BrandAvatar.vue'

// Icons
import FolderIcon from '../../components/globals/Icons/SpyderV2Icons/FolderIcon.vue'
import ChevronGrabberIcon from '../../components/globals/Icons/ChevronGrabberIcon.vue'
import ChevronIcon from '../../components/globals/Icons/ChevronIcon.vue'
import PlayIcon from '../../components/globals/Icons/PlayIcon.vue'
import AdChangelogIcon from '../../components/globals/Icons/SpyderV2Icons/AdChangelogIcon.vue'
import LinkIcon from '../../components/globals/Icons/LinkIcon.vue'
import SpyderIcon from '../../components/globals/Icons/SidebarIcons/SpyderIcon.vue'
import CommunityFeedIcon from '../../components/globals/Icons/DiscoveryIcons/CommunityFeedIcon.vue'

// View Icons
import * as SidebarIcons from '../../components/globals/Icons/SpyderModeIcons'
import * as ViewIcons from '../../components/globals/Icons/SpyderViewIcons'

// Views
import BrandOverviewView from '../../components/spyderV2/overview/BrandOverviewView.vue'
import TrackableBrandOverview from '../../components/spyderV2/overview/trackableBrand/TrackableBrandOverview.vue'
import CommunityAdsBrandView from '../../components/spyderV2/brandViews/CommunityAdsBrandView.vue'

// Tab modes
import AdLibraryBrandView from '../../components/spyderV2/brandViews/AdLibraryBrandView.vue'
import AdRankBrandView from '../../components/spyderV2/brandViews/AdRankBrandView.vue'
import CreativeTestsBrandView from '../../components/spyderV2/brandViews/CreativeTestsBrandView.vue'
import HooksBrandView from '../../components/spyderV2/brandViews/HooksBrandView.vue'
import LandingPagesBrandView from '../../components/spyderV2/brandViews/LandingPagesBrandView.vue'
import TimelineBrandView from '../../components/spyderV2/brandViews/TimelineBrandView.vue'
import PartnershipAdsBrandView from '../../components/spyderV2/brandViews/PartnershipAdsBrandView.vue'
import WinningAdsBrandView from '../../components/spyderV2/brandViews/WinningAdsBrandView.vue'

// Views that render on their own, without the sidebar modes
const STANDALONE_VIEWS = ['overview', 'community-ads']

const BRAND_STATES = {
  SUBSCRIBED: 'subscribed', // The user tracks the brand: the full feature set
  LOCKED: 'locked', // We hold ads for the brand, but the user doesn't track it
  TRACKABLE: 'trackable', // No ads yet, but its Meta ad library can be scraped once it's tracked
  UNTRACKABLE: 'untrackable' // No ads, and no Meta ad library to scrape
}

// The only views a trackable brand can show before we've collected its ads
const NO_DATA_VIEWS = ['overview', 'community-ads']

export default {
  name: 'SpyderView',
  components: {
    MainLayout,
    BrandOverviewView,
    TrackableBrandOverview,
    CommunityAdsBrandView,
    AdLibraryBrandView,
    AdRankBrandView,
    CreativeTestsBrandView,
    HooksBrandView,
    LandingPagesBrandView,
    TimelineBrandView,
    WinningAdsBrandView,
    PartnershipAdsBrandView,
    FixSpyderModal,
    TrackBrandModal,
    BrandAvatar,
    FolderIcon,
    ChevronGrabberIcon,
    PlayIcon,
    AdChangelogIcon,
    LinkIcon,
    ChevronIcon
  },
  mixins: [clickaway],
  data () {
    return {
      // Tab Control
      currentView: 'meta',
      currentMode: 'ad-library',
      navCollapsed: localStorage.getItem('spyderBrandNavCollapsed') === 'true',

      // Landing page to preselect when entering the landing pages mode
      pendingLandingPageUrl: null,

      // Data States
      brand: {},

      // Spyder Subscription States
      showSpyderFixModal: false,
      spyderLimit: 2,
      showTrackBrandModal: false,

      // Loading States
      loadingBrandInfo: true,

      // Page Header States
      previousRoute: null,
      brandSwitcherExpanded: false,
      folderSwitcherExpanded: false,
      brandLastUpdated: '',
      trackIcon: SpyderIcon,

      // Tutorial Modal States
      showTutorialModal: false,
      tutorialTimestamp: 74,
    }
  },
  watch: {
    getIsOverSpyderLimit: { // Tracking more brands than the plan allows -> make the user pick which to keep
      immediate: true,
      handler (isOver) {
        if (isOver) this.showSpyderFixModal = true
      }
    },
    currentMode () {
      this.syncRoutePath()
    },
    currentView () {
      this.syncRoutePath()
    },
    // Runs before the next render, so views a trackable brand can't show are gone before they're passed the brand
    isTrackableBrand (isTrackable) {
      if (isTrackable) this.leaveDataViews()
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getTeam', 'getUser', 'getSpyderLimit']),
    ...mapGetters('BoardsModule', ['getFolders']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions', 'getSpyderSubscriptionsLoading', 'getIsOverSpyderLimit', 'getBrandsListQuery']),
    ...mapGetters('SearchModule', ['getFilterCategories']),
    ...mapGetters('MiscModule', ['getBrandOpenView']),
    topbarViews () {
      return [
        {
          name: 'Overview',
          key: 'overview',
          icon: ViewIcons.SpyderOverviewIcon,
          isActive: this.currentView === 'overview',
          action: () => this.changeView('overview'),
          disabled: false
        },
        {
          name: 'Community Ads',
          key: 'community-ads',
          icon: CommunityFeedIcon,
          isActive: this.currentView === 'community-ads',
          action: () => this.changeView('community-ads'),
          disabled: false
        },
        {
          name: 'Meta',
          key: 'meta',
          icon: ViewIcons.SpyderMetaIcon,
          isActive: this.currentView === 'meta',
          action: () => this.changeView('meta'),
          disabled: !this.isViewAccessible('meta'),
          disabledText: 'Brand has no data yet'
        },
        {
          name: 'TikTok',
          key: 'tiktok',
          icon: ViewIcons.SpyderTikTokIcon,
          isActive: this.currentView === 'tiktok',
          action: () => this.changeView('tiktok'),
          disabled: true,
          disabledText: 'Coming soon'
        },
        {
          name: 'Instagram',
          key: 'instagram',
          icon: ViewIcons.SpyderInstagramIcon,
          isActive: this.currentView === 'instagram',
          action: () => this.changeView('instagram'),
          disabled: true,
          disabledText: 'Coming soon'
        },
        {
          name: 'LinkedIn',
          key: 'linkedin',
          icon: ViewIcons.SpyderLinkedInIcon,
          isActive: this.currentView === 'linkedin',
          action: () => this.changeView('linkedin'),
          disabled: true,
          disabledText: 'Coming soon'
        },
        {
          name: 'Google Ads',
          key: 'google',
          icon: ViewIcons.SpyderGoogleIcon,
          isActive: this.currentView === 'google',
          action: () => this.changeView('google'),
          disabled: true,
          disabledText: 'Coming soon'
        }
      ]
    },
    sidebarModes () {
      return [
        { 
          name: 'Ad Library',
          key: 'ad-library',
          icon: SidebarIcons.AdLibraryIcon,
          isActive: this.currentMode === 'ad-library',
          action: () => this.changeMode('ad-library')
        },
        {
          name: 'Ad Rankings',
          key: 'ad-rankings',
          icon: SidebarIcons.AdRankIcon,
          isActive: this.currentMode === 'ad-rankings',
          action: () => this.changeMode('ad-rankings')
        },
        {
          name: 'Creative Tests',
          key: 'creative-tests',
          icon: SidebarIcons.CreativeTestsIcon,
          isActive: this.currentMode === 'creative-tests',
          action: () => this.changeMode('creative-tests')
        },
        {
          name: 'Landing Pages',
          key: 'landing-pages',
          icon: SidebarIcons.LandingPagesIcon,
          isActive: this.currentMode === 'landing-pages',
          action: () => this.changeMode('landing-pages')
        },
        {
          name: 'Hooks',
          key: 'hooks',
          icon: SidebarIcons.HooksIcon,
          isActive: this.currentMode === 'hooks',
          action: () => this.changeMode('hooks')
        },
        {
          name: 'Timeline',
          key: 'timeline',
          icon: SidebarIcons.TimelineIcon,
          isActive: this.currentMode === 'timeline',
          action: () => this.changeMode('timeline')
        },
        {
          name: 'Partnership Ads',
          key: 'partnership-ads',
          icon: SidebarIcons.PartnershipAdsIcon,
          isActive: this.currentMode === 'partnership-ads',
          action: () => this.changeMode('partnership-ads')
        }
      ]
    },
    subscribedBrand () {
      return this.getSpyderSubscriptions.find(sub => sub.id === this.$route.params.id) || null
    },
    headerBrand () { // For toolbar rendering
      return this.loadingBrandInfo ? this.subscribedBrand : this.brand
    },
    userIsSubscribed () {
      // The loaded brand's own flag is current, while the store's list can still be loading (or out of date)
      if (this.brand?.id) return !!this.brand.is_subscribed
      return !!this.subscribedBrand
    },
    subscriptionStatusLoading () {
      // Neither the brand nor the subscriptions have loaded, so we can't tell yet if the brand is subscribed
      return !this.headerBrand && this.getSpyderSubscriptionsLoading
    },
    brandState () {
      if (this.userIsSubscribed) return BRAND_STATES.SUBSCRIBED
      // How much data we hold is only known once the brand loads
      if (!this.brand?.id) return null
      switch (getBrandAdDataState(this.brand)) {
        case BRAND_AD_DATA_STATES.TRACKABLE: return BRAND_STATES.TRACKABLE
        case BRAND_AD_DATA_STATES.UNTRACKABLE: return BRAND_STATES.UNTRACKABLE
        default: return BRAND_STATES.LOCKED
      }
    },
    isTrackableBrand () {
      return this.brandState === BRAND_STATES.TRACKABLE
    },
    folder () {
      if (!this.headerBrand) return {}
      return findFolderForBrand(this.getFolders, this.headerBrand) || { id: null, name: 'Default Folder' }
    },
    folderBrands () { // For toolbar navigation
      // Returns the other brands in the same folder as the current brand
      let brands
      if (!this.folder.id) { // No folder id means current brand is in default folder
        brands = getUnfolderedBrands(this.getSpyderSubscriptions, this.getFolders)
      } else {
        brands = getBrandsInFolder(this.getSpyderSubscriptions, this.folder)
      }
      return brands.filter(sub => sub.id !== this.headerBrand?.id).sort((a, b) => a.name.localeCompare(b.name))
    },
    folderRoutes () { // For toolbar navigation
      // Returns names and the first brand id of the other folders
      const routes = []
      this.getFolders.forEach(folder => {
        const brands = getBrandsInFolder(this.getSpyderSubscriptions, folder)
        if (brands.length < 1 || folder.id === this.folder.id) return // Skip the currently active folder and folders with no brands
        routes.push({
          name: folder.name,
          brandId: brands[0].id
        })
      })
      // Add the default folder (if we're not in it already)
      if (this.folder.id) {
        // Get the first brand that isn't in any folder
        const defaultBrand = getUnfolderedBrands(this.getSpyderSubscriptions, this.getFolders)?.[0]
        if (defaultBrand) {
          routes.push({
            name: 'Default Folder',
            brandId: defaultBrand.id
          })
        }
      }
      return routes
    },
    isStandaloneView () {
      return STANDALONE_VIEWS.includes(this.currentView)
    },
    showSidebarNav () {
      return !this.isStandaloneView
    },
    layoutMode () {
      // currentMode is kept while on a standalone view so switching back restores it,
      // so mode-specific layout rules (e.g. scroll locking) must not leak into standalone views
      return this.isStandaloneView ? this.currentView : this.currentMode
    },
    layoutStyleOverride () {
      if (this.layoutMode === 'landing-pages') return 'height: calc(100vh - 30px); overflow: hidden;'
      return ''
    },
    useStableScrollGutter () {
      return ['overview', 'community-ads', 'ad-library', 'ad-rankings', 'creative-tests', 'timeline', 'partnership-ads'].includes(this.layoutMode)
    }
  },
  beforeRouteEnter (to, from, next) {
    // Remember where the user came from for the back button (router-view is keyed by fullPath, so this runs on every brand switch)
    next(vm => { vm.previousRoute = from })
  },
  beforeMount () {
    const { view, mode } = this.$route.params
    // Links without a view (e.g. /library-spyder/:id) open on the view picked in Settings > Preferences
    this.currentView = view || this.getBrandOpenView
    if (mode) this.currentMode = mode
    // Links without both segments fall back to defaults, so reflect that in the path
    if (!view || (!STANDALONE_VIEWS.includes(view) && !mode)) this.syncRoutePath()
  },
  async mounted () {
    EventBus.$on('spyder:track-brand', this.openTrackBrandModal)
    this.spyderLimit = this.getSpyderLimit

    // The Sidebar fetches subscriptions on page load, so only refresh them (e.g. for a teammate's changes) once that's done.
    // The brand doesn't wait on them: once it loads, its own is_subscribed flag says whether the user tracks it
    if (!this.getSpyderSubscriptionsLoading) {
      this.fetchSpyderSubscriptions().catch(error => console.error('Error refreshing spyder subscriptions', error))
    }

    await this.getUniqueBrand()

    this.loadingBrandInfo = false
  },
  beforeDestroy () {
    EventBus.$off('spyder:track-brand', this.openTrackBrandModal)
  },
  methods: {
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions']),
    ...mapActions('BoardsModule', ['fetchFolders']),

    // ================================================================================
    // ============================== DATA FETCH METHODS ==============================
    // ================================================================================

    async getUniqueBrand () {
      this.brand = await ForeplayAPI.Brands.getByBrandId(this.$route.params.id)
    },

    // ================================================================================
    // ========================== BRAND SUBSCRIPTION METHODS ==========================
    // ================================================================================

    async handleBrandSubscribed () {
      try {
        this.loadingBrandInfo = true
        await Promise.all([
          this.fetchSpyderSubscriptions(),
          this.fetchFolders()
        ])
        this.brand = {}
        await this.getUniqueBrand()
        this.loadingBrandInfo = false
      } catch (error) {
        window.location.reload()
      }
    },
    openTrackBrandModal () {
      this.showTrackBrandModal = true
    },

    // ================================================================================
    // =========================== TAB/MODE CONTROL METHODS ===========================
    // ================================================================================

    // Sync the /library-spyder/:id/:view/:mode path without triggering a router reload
    syncRoutePath () {
      const path = this.isStandaloneView
        ? `/library-spyder/${this.$route.params.id}/${this.currentView}`
        : `/library-spyder/${this.$route.params.id}/${this.currentView}/${this.currentMode}`
      history.replaceState({}, null, path)
    },
    isViewAccessible (view) {
      return !this.isTrackableBrand || NO_DATA_VIEWS.includes(view)
    },
    // Trackable brands open on the Overview in place of the default view,
    // unless the link asked for one of the other views they can show
    leaveDataViews () {
      const linkedView = this.$route.params.view
      if (!linkedView || !this.isViewAccessible(this.currentView)) this.currentView = 'overview'
    },
    changeView (view) {
      if (!this.isViewAccessible(view)) return
      this.currentView = view
    },
    openMetaMode (mode) {
      if (!this.isViewAccessible('meta')) return
      this.currentView = 'meta'
      this.currentMode = mode
    },
    changeMode (mode, options = {}) {
      if (this.showSpyderFixModal) return
      this.pendingLandingPageUrl = options.landingPageUrl || null
      this.currentMode = mode
    },
    handleTabSwitch (payload) {
      if (typeof payload === 'string') {
        this.changeMode(payload)
        return
      }
      this.changeMode(payload.mode, { landingPageUrl: payload.landingPageUrl })
    },
    analyzeAdRanks () {
      this.openMetaMode('ad-rankings')
    },
    viewMetaAds () {
      this.openMetaMode('ad-library')
    },
    viewPartnerAds () {
      this.openMetaMode('partnership-ads')
    },

    // ================================================================================
    // ================================== UI METHODS ==================================
    // ================================================================================

    navigateBack () {
      // Going back to another brand would just step back through the brand switcher, so go to the brands list instead
      const cameFromOtherBrand = this.previousRoute?.name === 'SpyderView' && this.previousRoute.params?.id !== this.$route.params.id
      // If the user navigated here from within the app, go back
      if (this.previousRoute?.name && !cameFromOtherBrand) {
        this.$router.go(-1)
        return
      }
      // Otherwise (e.g. direct link or page refresh), go to the brands list, reopening the search it was left on
      this.$router.push({ name: 'SpyderBrandsView', query: this.getBrandsListQuery })
    },
    toggleNavCollapse () {
      this.navCollapsed = !this.navCollapsed
      localStorage.setItem('spyderBrandNavCollapsed', String(this.navCollapsed))

      // Signal UI components (e.g. AdvertisementList grids) to animate a reflow
      // once the nav width transition (300ms) has finished.
      setTimeout(() => {
        EventBus.$emit('sidebar:collapse-transition-end')
        setTimeout(() => { window.dispatchEvent(new Event('resize')) }, 1)
      }, 300)
    },
    onScroll (e, position) {
      // Forward scroll position to the active brand view so it can style its own sticky filter bar
      EventBus.$emit('spyder:scroll', position)
    },
    setBrandSwitcherExpanded (expanded) {
      this.brandSwitcherExpanded = expanded
      if (expanded) {
        this.$nextTick(() => {
          console.log(this.$refs)
          const spacer = this.$refs['brand-switcher-spacer']
          const switcher = this.$refs['brand-switcher']
          console.log(spacer, switcher)
          if (spacer && switcher) {
            spacer.style.width = `${switcher.offsetWidth - 8}px`
          }
        })
      }
    },
    setFolderSwitcherExpanded (expanded) {
      this.folderSwitcherExpanded = expanded
      if (expanded) {
        this.$nextTick(() => {
          const spacer = this.$refs['folder-switcher-spacer']
          const switcher = this.$refs['folder-switcher']
          if (spacer && switcher) {
            spacer.style.width = `${switcher.offsetWidth - 9}px`
          }
        })
      }
    },
    getBrandLastUpdated () {
      if (!this.headerBrand?.last_checked_at) return 'Not Tracked'
      const time = moment.utc(this.headerBrand.last_checked_at).unix()
      const updatedAgo = ((new Date().getTime() - (time * 1000)) / 3600000)
      const timeStr = updatedAgo < 1 ? `${Math.floor(updatedAgo * 60)}m` : `${Math.floor(updatedAgo)}h`
      return `Updated ${timeStr} ago`
    },
    formatDate (dateUTC) {
      if (!dateUTC) return ''
      const date = new Date(dateUTC)
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return date.toLocaleDateString('en-US', options)
    }
  },
  metaInfo: {
    title: 'Foreplay.co | Spyder'
  }
}
</script>

<style scoped>
.brand-sidebar-nav {
  position: relative;
  display: flex;
  flex-direction: column;
  top: 0px;
  width: 180px;
  flex-shrink: 0;
  z-index: 40001;
  background-color: white;
  transition: width 300ms ease-in-out;
}
.brand-sidebar-nav.collapsed {
  width: 52px;
}
.brand-sidebar-nav .nav-body {
  position: sticky;
  top: 0px;
}
.brand-sidebar-nav .nav-btn-text {
  transition: opacity 300ms ease-in-out;
  opacity: 1;
}
.brand-sidebar-nav.collapsed .nav-btn-text {
  opacity: 0;
}
.brand-sidebar-nav .nav-footer {
  position: sticky;
  bottom: 0px;
  margin-top: auto;
}
.brand-sidebar-nav .collapse-toggle-container {
  position: sticky;
  top: calc((100vh - 120px) / 2);
  left: 100%;
  width: 0px;
  height: 0px;
  background-color: green;
}
.collapse-toggle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: white;
  border: 1px solid;
  border-color: #ECEFF3;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: background-color 100ms ease-in-out, border-color 100ms ease-in-out;
}
.collapse-toggle:hover {
  background-color: #F6F8FA;
  border-color: #DFE1E6;
}

.brand-view-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678; /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none; /* Standard */
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
}
.brand-view-tab:hover {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.brand-view-tab.active {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
  font-weight: 500;
}
.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
.brand-route-switcher {
  transition: box-shadow 150ms ease-in-out;
  box-shadow: none;
  cursor: default;
}
.brand-route-switcher.enabled {
  cursor: pointer;
}
.brand-route-switcher.enabled:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.brand-switcher-dropdown {
  z-index: 40002;
}
.box-shadow {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.timeaxis-line--day {
  margin-top: 30px;
  margin-left: 3px;
}
.timeaxis-line--month {
  margin-top: 70px;
}
.hooks-bg {
  background-color: #F6F8FA;
}
.arrow-top {
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 7px solid #06070F;
}
</style>
